/* global Office, Word, console */

import { XMLBuilder, XMLParser } from "fast-xml-parser";
import { CoObjectItem, DimensionDetails } from "../../components/interfaces/CoObject";
import { getActiveDocumentPartIncludeContentControlIds } from "../../api/object/CoObjectGeneral";
import { CopyCutPasteInstance } from "../../office/CopyCutPaste";

export const CO_OBJECT_NAMESPACE = "CustomXMLCo";

export enum DocPropKeys {
  TenantId = "Tenant-ID",
  DocumentId = "Document-ID",
}

function fixParsedDimensionsIfPresent(xmlObject) {
  if (xmlObject.source?.dimensions || xmlObject.source?.dimensions === "") {
    let dimensionsArray: DimensionDetails[] = [];
    if (typeof xmlObject.source.dimensions !== "string" && !(xmlObject.source.dimensions instanceof String)) {
      if (Array.isArray(xmlObject.source.dimensions)) dimensionsArray = xmlObject.source.dimensions;
      else dimensionsArray = [xmlObject.source.dimensions];
    }

    xmlObject.source.dimensions = dimensionsArray.map((d) => {
      return {
        ...d,
        values: (Array.isArray(d.values) ? d.values : [d.values]).map((v) => {
          if (Array.isArray(v.oidPath)) return v;
          else return { ...v, oidPath: [v.oidPath] };
        }),
        hierarchy: (d.hierarchy as any) === "" ? null : d.hierarchy,
      };
    });
  }
}
function fixParsedParametersIfPresent(xmlObject) {
  if (xmlObject.parameters || xmlObject.parameters === "") {
    let parametersArray: DimensionDetails[] = [];
    if (typeof xmlObject.parameters !== "string" && !(xmlObject.parameters instanceof String)) {
      if (Array.isArray(xmlObject.parameters)) parametersArray = xmlObject.parameters;
      else parametersArray = [xmlObject.parameters];
    }

    xmlObject.parameters = parametersArray.map((d) => {
      return {
        ...d,
        code: d.code?.toString(),
        values: (Array.isArray(d.values) ? d.values : [d.values]).map((v) => {
          if (Array.isArray(v.oidPath)) return v;
          else return { ...v, oidPath: [v.oidPath] };
        }),
      };
    });
  }
  //IDS-3057 some objects' XML from import of an old CO document may have an extra <chart/> tag after the main one
  if (xmlObject.source?.chart && Array.isArray(xmlObject.source.chart)) {
    xmlObject.source.chart = xmlObject.source.chart[0];
  }
}

const option = { ignoreAttributes: false, numberParseOptions: { leadingZeros: false, hex: true } };

export function buildObjectFromXMLString(xml: string): CoObjectItem | null {
  const xmlObject = new XMLParser(option).parse(xml)[CO_OBJECT_NAMESPACE];
  if (!xmlObject?.contentControlId) return null;

  fixParsedDimensionsIfPresent(xmlObject);
  fixParsedParametersIfPresent(xmlObject);
  return xmlObject;
}

// TODO see method below
export const getCustomXmlByNamespaceId = async (contentControlId: string): Promise<CoObjectItem | null> => {
  return new Promise((resolve) => {
    const key = CO_OBJECT_NAMESPACE + contentControlId;
    try {
      Office.context.document.customXmlParts.getByNamespaceAsync(key, function (result) {
        const xmlPart = result.value[0];
        if (xmlPart) {
          xmlPart.getXmlAsync({}, function (eventArgs) {
            resolve(buildObjectFromXMLString(eventArgs.value));
          });
        } else resolve(null);
      });
    } catch (err) {
      console.error("customXmlParts not found ERROR", err);
      resolve(null);
    }
  });
};

//TODO the following Promise construction should substitute the above getCustomXmlByNamespaceId
// because the following propagates the error
export const getCustomXmlOrThrow = async (contentControlId: string): Promise<CoObjectItem | null> => {
  return new Promise((resolve, reject) => {
    const key = CO_OBJECT_NAMESPACE + contentControlId;
    try {
      Office.context.document.customXmlParts.getByNamespaceAsync(key, function (result) {
        const xmlPart = result.value[0];
        if (xmlPart) {
          xmlPart.getXmlAsync({}, function (eventArgs) {
            resolve(buildObjectFromXMLString(eventArgs.value));
          });
        } else resolve(null);
      });
    } catch (err) {
      console.error("customXmlParts not found ERROR", err);
      reject(err);
    }
  });
};

export const retrieveDocumentProp = async (key: string): Promise<string> => {
  return new Promise((resolve) => {
    try {
      Word.run(async (context) => {
        context.document.properties.customProperties.getCount();
        const docProp = context.document.properties.customProperties.getItemOrNullObject(key);
        docProp.load("value");
        await context.sync();
        console.log(`docProp ${key}: ${docProp.value}`);
        resolve(docProp.value);
      });
    } catch (e) {
      console.log(e);
    }
  });
};

export const addContentControlCustomXml = (contentControlId: string, dataObject: any) =>
  new Promise<void>((resolve, reject) => {
    try {
      const xml = new XMLBuilder({ ignoreAttributes: false }).build(JSON.parse(JSON.stringify(dataObject)));
      console.log("addCustomXML: Calling Office API customXmlParts.addAsync with:", { contentControlId, xml });
      var xmlString = `<CustomXMLCo xmlns="${CO_OBJECT_NAMESPACE}${contentControlId}">${xml}</CustomXMLCo>`;

      Office.context.document.customXmlParts.addAsync(xmlString, (asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
          console.error("XML was not added in the document", {
            asyncResult,
            errorMessage: asyncResult.error.message,
            xmlString: xmlString,
          });
          reject(asyncResult.error);
        } else {
          console.log(`CustomXml of ccId ${contentControlId} added.`);
          resolve();
        }
      });
    } catch (e) {
      console.error("Error: XML was not added in the document", {
        e,
        xmlString: xmlString,
      });
      reject(e);
    }
  });

export function deleteXMLPart(contentControlId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      var key = CO_OBJECT_NAMESPACE + contentControlId;
      console.log("deleteCustomXML Calling Office API customXmlParts.getByNamespaceAsync with ", {
        contentControlId,
        key,
      });
      Office.context.document.customXmlParts.getByNamespaceAsync(key, function (result) {
        if (result.error) {
          console.error("XML was not retrieved/deleted", result.error, result);
          reject(result.error);
        } else {
          if (result.value.length == 0) {
            console.warn("No XML was retrieved for deletion", result);
            resolve();
          } else {
            result.value.forEach((xmlPart) =>
              xmlPart.deleteAsync(function (eventArgs) {
                console.log("XML Part deleted.", contentControlId, key, eventArgs.value);
                resolve();
              })
            );
          }
        }
      });
    } catch (e) {
      console.error("Error: XML was not retrieved/deleted", e, contentControlId, key);
      reject(e);
    }
  });
}

export async function editXMLPart(contentControlId: string, dataObject: any) {
  return new Promise((resolve, reject) => {
    try {
      console.log("editXMLPart", contentControlId, dataObject);
      deleteXMLPart(contentControlId).then(() =>
        addContentControlCustomXml(contentControlId, dataObject).then(resolve)
      );
    } catch (e) {
      console.error("editXMLPart failed", e, contentControlId, dataObject);
      reject(e);
    }
  });
}

export const getAllCoObjects = async (documentId: string, filterPartsNotOnDB?: boolean): Promise<CoObjectItem[]> => {
  return new Promise((resolve) => {
    Word.run(async (context) => {
      // we load cc from document content and map then to their position
      const ccPosition = new Map<number, number>();
      const contentControls = context.document.contentControls;
      contentControls.load("items");
      await context.sync();
      contentControls.items.forEach((ccItem, index) => {
        ccPosition.set(ccItem.id, index);
      });
      // we load custom xml with object data
      // same parts could refer to cc no more present
      // in document content, so we trace then to remove
      let coObjects: CoObjectItem[] = [];
      const xmlPartsToRemove: Word.CustomXmlPart[] = [];
      context.document.customXmlParts.load("items");
      await context.sync();
      const coXmlParts = context.document.customXmlParts.items.filter((part) =>
        part.namespaceUri.startsWith(CO_OBJECT_NAMESPACE)
      );
      for (const part of coXmlParts) {
        part.load("xml");
        const xml = part.getXml();
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync();
        // eslint-disable-next-line office-addins/load-object-before-read
        const coObject = buildObjectFromXMLString(xml.value);
        if (coObject) {
          // we are interested only xml for coObject (0 is false)
          if (ccPosition.get(Number(coObject.contentControlId)) !== undefined) {
            coObjects.push(coObject);
          } else {
            //we remove from custom xml
            const ccsSelected = CopyCutPasteInstance.getCCsSelected();
            if (!ccsSelected.find((el) => el.id === Number(coObject.contentControlId))) xmlPartsToRemove.push(part);
          }
        }
      }
      // we delete xmlPart no more used in content document;
      if (xmlPartsToRemove.length > 0) {
        for (const part of xmlPartsToRemove) {
          part.delete();
          console.log("deleted custom xml part for object:", part.toJSON());
        }
        await context.sync();
      }

      if (filterPartsNotOnDB) {
        const dbCcIds = await getActiveDocumentPartIncludeContentControlIds(documentId);
        coObjects = coObjects.filter((coObject) =>
          dbCcIds.some((dbCcId) => dbCcId.toString() == coObject.contentControlId.toString())
        );
      }

      // we sort returned object respecting document content
      resolve(
        coObjects.sort((a, b) => {
          return ccPosition.get(Number(a.contentControlId)) - ccPosition.get(Number(b.contentControlId));
        })
      );
    });
  });
};

export function getCCByDocPartId(documentId: string, docPartId: string) {
  return getAllCoObjects(documentId).then((objects) =>
    objects.find(
      (el) =>
        el.info.objectType === "DOCUMENT_PART_INCLUDE" &&
        (el.source as any as { path: string; includedDocumentPartId: string }).includedDocumentPartId === docPartId
    )
  );
}
